import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Shield, Lock, Calendar } from 'lucide-react';
import { useFormContext, Controller } from 'react-hook-form';

export function ContractSettings() {
  const today = new Date().toISOString().split('T')[0];
  const { control, watch, formState } = useFormContext();
  const contractStart = watch('contractStartDate');
  const contractScope = watch('contractScope');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Contract Configuration
        </CardTitle>
        <CardDescription>
          Set contract duration and access scope for this property administrator
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-medium text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Contract Dates
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contractStartDate">Start Date</Label>
              <Controller
                control={control}
                name="contractStartDate"
                render={({ field }) => (
                  <Input
                    id="contractStartDate"
                    type="date"
                    min={today}
                    {...field}
                  />
                )}
              />
              {formState.errors.contractStartDate && (
                <p className="text-sm text-destructive">
                  {String(
                    formState.errors.contractStartDate?.message ||
                      'Invalid date'
                  )}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractEndDate">End Date</Label>
              <Controller
                control={control}
                name="contractEndDate"
                render={({ field }) => (
                  <Input
                    id="contractEndDate"
                    type="date"
                    min={contractStart || today}
                    {...field}
                  />
                )}
              />
              {formState.errors.contractEndDate && (
                <p className="text-sm text-destructive">
                  {String(
                    formState.errors.contractEndDate?.message || 'Invalid date'
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            {contractStart && watch('contractEndDate') && (
              <p>
                Contract period:{' '}
                <strong>{new Date(contractStart).toLocaleDateString()}</strong>{' '}
                to{' '}
                <strong>
                  {new Date(watch('contractEndDate')).toLocaleDateString()}
                </strong>
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-medium text-sm flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Access Scope
          </h3>

          <div className="space-y-2">
            <Label htmlFor="contractScope">Contract Scope</Label>
            <Controller
              control={control}
              name="contractScope"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select access scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <div className="space-y-1">
                        <div className="font-medium">Full Access</div>
                        <div className="text-xs text-muted-foreground">
                          Access to all departments and touchpoints
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="limited">
                      <div className="space-y-1">
                        <div className="font-medium">Limited Access</div>
                        <div className="text-xs text-muted-foreground">
                          Access to specific departments and touchpoints only
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {formState.errors.contractScope && (
              <p className="text-sm text-destructive">
                {String(
                  formState.errors.contractScope?.message || 'Invalid selection'
                )}
              </p>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {contractScope === 'full' && (
              <p>✓ Administrator will have access to all assessment areas</p>
            )}
            {contractScope === 'limited' && (
              <p>
                {' '}
                You will need to select specific departments and touchpoints
                below
              </p>
            )}
          </div>
        </div>

        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Note:</strong> The contract settings determine the
            administrator's access level and contract validity period. These can
            be modified later if needed.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
